// Caller sentiment trend (console v3 sentiment piece, 2026-09-21) — the PURE
// half: no React, no DOM, so it is testable in node and survives any layout
// redesign unchanged. The component that draws it (components/Sentiment/
// CallerSentiment.jsx) only renders the model this file returns.
//
// What the data can support, and what it cannot (sizing, 2026-09-21):
// - Contact Lens real-time gives ONE categorical label per utterance
//   (POSITIVE / NEUTRAL / NEGATIVE) and no numeric score. So each caller line
//   is -1 / 0 / +1, lightly smoothed; nothing finer is implied.
// - Only the agent leg is analysed, so the series starts when the agent does.
// - The trend sentence states a SEQUENCE ("Easing since 2:14, after you said
//   '…'"), never a cause. Every word is traceable to the transcript. Only an
//   easing sentence quotes the agent; a slipping one gives the time alone.
//
// Sparkline-scale on purpose (Storm, 2026-09-21): a live call is high
// cognitive load and three coarse categories do not support a chart. The
// model is capped at MAX_BARS and carries no axes, scale or legend data.

export const VALUE = { NEGATIVE: -1, NEUTRAL: 0, POSITIVE: 1 };
export const MAX_BARS = 12;          // last N caller lines shown
export const SMOOTH_WINDOW = 3;      // trailing mean over this many lines
export const MIN_LINES_FOR_TREND = 4;
export const TREND_DELTA = 0.5;      // recent-vs-earlier mean change that counts as a trend
export const QUOTE_MAX = 60;
// Caller lines shorter than this carry too little signal to stand for the
// caller's state (vault #213, Storm 2026-09-21): on a real call a one-word
// NEUTRAL reply two seconds before hang-up turned a sustained "Slipping" into
// "Steady, mostly neutral". They are left out of the label, the bars and the
// sentence — the trend should reflect the call, not its last syllable.
export const MIN_WORDS = 3;

const LABEL_WORD = { POSITIVE: 'positive', NEUTRAL: 'neutral', NEGATIVE: 'negative' };

export const wordCount = text => String(text || '').trim().split(/\s+/).filter(Boolean).length;

// Labelled caller lines, in call order. Unlabelled lines are skipped, never
// treated as neutral (same rule as the pills since Phase 3). Short replies
// (under MIN_WORDS) are skipped too unless { includeShort: true }.
export function callerPoints(lines, { includeShort = false } = {}) {
  return (lines || [])
    .filter(l => l && l.role === 'CUSTOMER' && typeof l.sentiment === 'string' && VALUE[l.sentiment] !== undefined)
    .filter(l => includeShort || wordCount(l.text) >= MIN_WORDS)
    .map(l => ({ id: l.id, v: VALUE[l.sentiment], label: l.sentiment, beginMs: Number(l.beginMs) || 0, time: l.time ?? null }));
}

// Trailing mean: each point averaged with up to SMOOTH_WINDOW-1 before it.
export function smooth(values, window = SMOOTH_WINDOW) {
  return values.map((_, i) => {
    const w = values.slice(Math.max(0, i - window + 1), i + 1);
    return w.reduce((a, b) => a + b, 0) / w.length;
  });
}

const mean = a => (a.length ? a.reduce((x, y) => x + y, 0) / a.length : 0);

function quote(text) {
  const t = String(text || '').replace(/\s+/g, ' ').trim();
  if (!t) return null;
  if (t.length <= QUOTE_MAX) return t;
  const cut = t.slice(0, QUOTE_MAX);
  const sp = cut.lastIndexOf(' ');
  return `${(sp > 20 ? cut.slice(0, sp) : cut).replace(/[,;:.\s]+$/, '')}…`;
}

// Direction of the recent caller lines against the ones before them, and where
// the current run of that direction began. Returns null until there are enough
// lines to say anything honest.
export function trendOf(points) {
  if (points.length < MIN_LINES_FOR_TREND) return null;
  const v = points.map(p => p.v);
  const recent = v.slice(-3);
  const earlier = v.slice(Math.max(0, v.length - 6), v.length - 3);
  const delta = mean(recent) - mean(earlier);
  if (Math.abs(delta) < TREND_DELTA) {
    const counts = {};
    for (const p of points.slice(-3)) counts[p.label] = (counts[p.label] || 0) + 1;
    const top = Math.max(...Object.values(counts));
    const mostly = [...points.slice(-3)].reverse().find(p => counts[p.label] === top).label;
    return { direction: 'steady', mostly };
  }
  const up = delta > 0;
  // Walk back over the current monotonic run, then take its first real step.
  let s = v.length - 1;
  while (s > 0 && (up ? v[s - 1] <= v[s] : v[s - 1] >= v[s])) s -= 1;
  let k = -1;
  for (let i = Math.max(1, s + 1); i < v.length; i++) {
    if (up ? v[i] > v[i - 1] : v[i] < v[i - 1]) { k = i; break; }
  }
  return { direction: up ? 'easing' : 'slipping', turnIndex: k };
}

// The agent's last line between the caller line before the turn and the turn
// itself — what was said immediately before the change. Sequence, not cause.
function agentLineBefore(lines, prevBeginMs, turnBeginMs) {
  const agent = (lines || []).filter(l => l && l.role === 'AGENT' && typeof l.text === 'string'
    && (Number(l.beginMs) || 0) < turnBeginMs && (Number(l.beginMs) || 0) >= prevBeginMs);
  return agent.length ? agent[agent.length - 1] : null;
}

export function trendSentence(lines, points = callerPoints(lines)) {
  const t = trendOf(points);
  if (!t) return null;
  if (t.direction === 'steady') return `Steady, mostly ${LABEL_WORD[t.mostly]}`;
  const word = t.direction === 'easing' ? 'Easing' : 'Slipping';
  if (t.turnIndex < 1) return word;
  const turn = points[t.turnIndex];
  const before = points[t.turnIndex - 1];
  const since = turn.time ? ` since ${turn.time}` : '';
  // The agent's words are quoted only when the caller is EASING. On a slip,
  // "after you said X" reads mid-call as "you caused this", even though it only
  // states order — not a risk worth taking in a tool meant to help the agent
  // (Storm, 2026-09-21). "Slipping since 0:30" alone says what matters.
  if (t.direction !== 'easing') return `${word}${since}`;
  const said = agentLineBefore(lines, before.beginMs, turn.beginMs);
  const q = said ? quote(said.text) : null;
  return q ? `${word}${since}, after you said '${q}'` : `${word}${since}`;
}

// The whole caller model the component renders. null until the caller has a
// labelled line. bars: newest last, each { v: smoothed -1..1, sign }.
export function callerSentimentModel(lines) {
  const points = callerPoints(lines);
  if (!points.length) {
    // Early in a call every caller line may still be short ("hi", "yes"). Show
    // the latest label so the row is not blank, but draw no bars and say no
    // trend — there is nothing substantive to read yet.
    const any = callerPoints(lines, { includeShort: true });
    if (!any.length) return null;
    const label = any[any.length - 1].label;
    return { label, bars: [], sentence: null, ariaLabel: `Caller sentiment ${LABEL_WORD[label]}.` };
  }
  const smoothed = smooth(points.map(p => p.v)).slice(-MAX_BARS);
  const bars = smoothed.map(v => ({ v, sign: v > 0.17 ? 'positive' : v < -0.17 ? 'negative' : 'neutral' }));
  const label = points[points.length - 1].label;
  const sentence = trendSentence(lines, points);
  const shown = points.slice(-MAX_BARS);
  const n = k => shown.filter(p => p.label === k).length;
  const ariaLabel = `Caller sentiment ${LABEL_WORD[label]}. Last ${shown.length} caller line${shown.length === 1 ? '' : 's'}: `
    + `${n('POSITIVE')} positive, ${n('NEUTRAL')} neutral, ${n('NEGATIVE')} negative.${sentence ? ` ${sentence}.` : ''}`;
  return { label, bars, sentence, ariaLabel };
}
